import cx from 'classnames'
import { Field, FieldValidator } from 'formik'
import * as React from 'react'

type TextInputProps = {
  name: string
  label?: React.ReactNode
  placeholder?: string
  type?: string
  help?: string | React.ElementType
  helpFn?: (arg0: string) => React.ReactNode
  addons?: React.ReactNode
  className?: string
  labelClassName?: string
  maxLength?: number
  validate?: FieldValidator
  leftIcon?: React.ReactNode
  transform?: (val: string) => string
  component?: 'input' | 'textarea'
  children?: React.ReactNode
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      label,
      placeholder,
      type = 'text',
      help,
      helpFn,
      addons,
      className,
      labelClassName = 'label is-size-7 has-text-weight-light has-text-left',
      maxLength,
      validate,
      leftIcon,
      transform,
      component,
      children,
      ...props
    }: TextInputProps,
    ref
  ) => {
    const Input = component || 'input'

    return (
      <Field name={name} validate={validate}>
        {({ field: { onChange, ...field }, meta, form }) => (
          <div className="field">
            {label && (
              <label htmlFor={`input-${name}`} className={labelClassName}>
                {label}
              </label>
            )}
            <div className="mt-1">{children}</div>
            <div
              className={cx('field', {
                'has-addons': addons,
              })}
            >
              <div
                className={cx('control is-expanded', {
                  'has-icons-left': !!leftIcon,
                })}
              >
                <Input
                  id={`input-${name}`}
                  className={cx(
                    'input',
                    {
                      'is-danger': meta.touched && meta.error,
                    },
                    className
                  )}
                  type={type}
                  onChange={(e) => {
                    if (transform) {
                      form.setFieldValue(name, transform(e.currentTarget.value))
                      return
                    }

                    onChange(e)
                  }}
                  {...field}
                  {...props}
                  ref={ref}
                  placeholder={placeholder ? placeholder : (label as string)}
                  maxLength={maxLength}
                />
                {leftIcon && <span className="icon is-left">{leftIcon}</span>}
                {meta.touched && meta.error && <p className="help is-danger">{meta.error}</p>}
                {help && <p className="help">{help}</p>}
                {helpFn && helpFn(field.value)}
              </div>
              {addons && <div className="control">{addons}</div>}
            </div>
          </div>
        )}
      </Field>
    )
  }
)

export const PasswordInput = ({ ...props }: Omit<TextInputProps, 'type'>) => {
  const [isShown, setIsShown] = React.useState(false)

  return (
    <TextInput
      type={isShown ? 'text' : 'password'}
      addons={
        <button
          className="button is-info"
          onClick={(e) => {
            e.preventDefault()
            setIsShown((prev) => !prev)
          }}
        >
          {isShown ? 'Hide' : 'Show'}
        </button>
      }
      {...props}
    />
  )
}

export default TextInput
